import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
} from '@angular/core';
import { AutoLotView } from 'src/app/const';
import { LotWatchRequestInspectionData } from 'src/app/services/auction-video-watch/types/lot-watch-request-inspection-data.type';
import { Limits } from 'src/app/services/limits.service';

import {
  Bid,
  LotSource,
  LotTranslation,
  LotTypeEnum,
  UserLotMark,
  UserLotQuery,
} from '../../graphql/service/graphql-auto-main-service';
import { LotActionEnum } from './lot-action.enum';

@Component({
  selector: 'app-lot-actions',
  templateUrl: './lot-actions.component.html',
  styleUrls: ['./lot-actions.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LotActionsComponent implements OnChanges {
  @Input() lot: AutoLotView;
  @Input() lotType: LotTypeEnum;
  @Input() lotSource: LotSource;
  @Input() isAraiKenki = false;
  @Input() aiTranslationEnabled = false;
  @Input() hasTranslations = false;
  @Input() hasManualTranslations = false;
  @Input() translations: LotTranslation[] = [];
  @Input() isSanctioned = false;
  @Input() limits: Limits;
  @Input() bids: Bid[];
  @Input() savedLot: UserLotQuery['lot'];
  @Input() userLotMarks: UserLotMark[];
  @Input() currentUser: UserLotQuery['currentUser'];
  @Input() currentUserLimitsOverrideAvailable = false;
  @Input() currentUserLastBid?: UserLotQuery['lot']['currentUserLastBid'];
  @Input() auctionViewWatchRequestLoading?: boolean;
  @Input() auctionViewWatchRequestData?: LotWatchRequestInspectionData;

  @Output() actionCompleted = new EventEmitter<void>();
  @Output() scrollToSales = new EventEmitter<void>();
  @Output() scrollToTranslations = new EventEmitter<void>();

  actionsDrawerOpened = false;

  private customActionAvailableCondition: Partial<Record<LotActionEnum, boolean>> = {};
  private readonly unavailableActionsByLotType: Record<LotActionEnum & 'kenki', LotActionEnum[]> = {
    kenki: [LotActionEnum.Bids, LotActionEnum.Reset, LotActionEnum.CreateTranslationRequest],
    [LotTypeEnum.Oneprice]: [LotActionEnum.Bids, LotActionEnum.Reset],
    [LotTypeEnum.Moto]: [LotActionEnum.Notations],
  };

  LotActionEnum = LotActionEnum;

  constructor() {}

  ngOnChanges(): void {
    this.customActionAvailableCondition = {
      [LotActionEnum.PlaceBid]: !!this.lot && !this.isSanctioned,
      [LotActionEnum.NegotiationsPrice]:
        this.lotType === LotTypeEnum.Auto &&
        !this.isAraiKenki &&
        !!this.currentUser?.isEmployee &&
        !!(this.savedLot?.canOpenNegotiations || this.savedLot?.canEditNegotiationsPrice),
      [LotActionEnum.Bids]: this.currentUser?.canSeeUsersBids,
      [LotActionEnum.Reset]: !!this.lot?.isLotDataChanged,
      [LotActionEnum.PurchaseReport]: !!this.currentUser?.isAuctioneer,
      [LotActionEnum.WatchRequest]: this.canCreateWatchRequest,
      [LotActionEnum.AddCarInspectionAdmin]: !this.isSanctioned && this.isManager,
    };
  }

  isActionAvailable(action: LotActionEnum): boolean {
    const unavailableActions = <LotActionEnum[]>(
      (this.unavailableActionsByLotType[this.isAraiKenki ? 'kenki' : this.lotType] || [])
    );

    if (unavailableActions.includes(action)) {
      return false;
    }

    return this.customActionAvailableCondition[action] ?? true;
  }

  private get canCreateWatchRequest(): boolean {
    return !this.isSanctioned && !(this.auctionViewWatchRequestData?.requestCompleted ?? true);
  }

  get isManager(): boolean {
    return (
      this.currentUser?.isAuctioneer ||
      this.currentUser?.isTranslator ||
      this.currentUser?.isEmployee
    );
  }
}
